import { openInNewTab } from '@strata/helper-functions';
import { IconDropdownMenu } from '@strata/react-dropdown-menu';
import { SuccessToast } from '@strata/react-toast-message';
import { useNavigate } from 'react-router';
import type * as Yup from 'yup';

import { getIntakeSourceFile } from 'api/intakeCaseAPI';
import { IntakeSource } from 'services/constants';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useAppDispatch } from 'store';
import {
    type IntakeSourceSummary,
    usePutArchiveIntakeSourceMutation,
} from 'store/api/intakeCaseApi';
import { FormSections, type IntakeFormSectionSchemas } from 'store/intakeForm/intakeFormSchema';
import { updateCapturedData } from 'store/intakeForm/intakeFormSlice';
import { resetForm } from 'store/intakeForm/intakeFormThunks';

interface ActiveIntakeSourceActionsMenuProps {
    intakeSourceId: string;
    intakeSource: IntakeSourceSummary;
}

export const ActiveIntakeSourceActionsMenu = ({
    intakeSourceId,
    intakeSource,
}: ActiveIntakeSourceActionsMenuProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [putArchive] = usePutArchiveIntakeSourceMutation();

    return (
        <IconDropdownMenu
            menuItems={[
                {
                    text: 'View',
                    onItemClick: () => {
                        (async () => {
                            try {
                                openInNewTab(
                                    URL.createObjectURL(
                                        await getIntakeSourceFile({ intakeSourceId }),
                                    ),
                                );
                            } catch (err) {
                                handleError('Error downloading file.', err as Error);
                            }
                        })();
                    },
                },
                {
                    text: 'Open With Intake Form',
                    onItemClick: () => {
                        (async () => {
                            try {
                                const intakeSourceFile = await getIntakeSourceFile({
                                    intakeSourceId,
                                });
                                openInNewTab(URL.createObjectURL(intakeSourceFile));
                                dispatch(resetForm());
                                dispatch(
                                    updateCapturedData({
                                        [FormSections.POINT_OF_CONTACT]: {
                                            intakeSource: IntakeSource.FAX,
                                            contactFaxNumber: intakeSource.fax_number,
                                            faxAttachment: [
                                                {
                                                    name: intakeSourceFile.name,
                                                    size: intakeSourceFile.size,
                                                    isUploading: false,
                                                },
                                            ],
                                            uploadedFaxAttachment: [intakeSourceFile],
                                            intakeSourceId,
                                        } as Yup.InferType<
                                            (typeof IntakeFormSectionSchemas)[typeof FormSections.POINT_OF_CONTACT]
                                        >,
                                    }),
                                );
                                navigate(AppRoutes.intakeForm);
                            } catch (err) {
                                handleError('Error downloading file.', err as Error);
                            }
                        })();
                    },
                },
                {
                    text: 'Archive',
                    onItemClick: () => {
                        (async () => {
                            try {
                                await putArchive(intakeSourceId).unwrap();
                                SuccessToast({
                                    message: 'Intake source archived successfully.',
                                });
                            } catch (_err) {
                                // Handled by mutation error handler
                            }
                        })();
                    },
                },
            ]}
        />
    );
};
